/**
 * Keycloak Auth Utilities — redirect to Keycloak login, callback handling, token storage.
 */

import { tokenStorage } from '@/lib/api/client';
import { authEndpoints } from '@/lib/api/endpoints';
import { api } from '@/lib/api/client';
import type { AuthTokens, MfaRequiredResponse, User } from '@/lib/api/types';

// ============================================================
// Configuration from environment
// ============================================================

const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_URL || '';
const KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM || '';
const KEYCLOAK_CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || '';

/**
 * Resolve the redirect URI for Keycloak.
 *
 * In development, Vite may auto-increment the port (5173 → 5174 → 5175…) if the
 * default port is already in use.  If the env var VITE_KEYCLOAK_REDIRECT_URI is
 * set to a fixed localhost:5173 URL but Vite is actually serving on a different
 * port, Keycloak would redirect back to the wrong port after login.
 *
 * Strategy:
 *  1. If the env var is NOT set → use `window.location.origin` (always correct).
 *  2. If the env var IS set and points to localhost in dev → use `window.location.origin`
 *     instead, because the env var may have a stale port.
 *  3. If the env var IS set and points to a production URL → use it as-is.
 */
function resolveRedirectUri(): string {
  const envUri = import.meta.env.VITE_KEYCLOAK_REDIRECT_URI as string | undefined;

  if (!envUri) {
    return `${window.location.origin}/auth/callback`;
  }

  // In development: if the env URI points to localhost but the current origin
  // differs (different port), trust window.location.origin instead.
  const envOrigin: string | null = (() => {
    try {
      return new URL(envUri).origin;
    } catch {
      return null;
    }
  })();

  if (
    envOrigin &&
    envOrigin.startsWith('http://localhost') &&
    envOrigin !== window.location.origin
  ) {
    // Dev mode: env has a localhost URL but port doesn't match actual server.
    // Use the real origin to avoid redirect-to-wrong-port bug.
    return `${window.location.origin}/auth/callback`;
  }

  return envUri;
}

/**
 * Resolve the post-logout redirect URI (same logic as resolveRedirectUri).
 */
function resolvePostLogoutUri(): string {
  const envUri = import.meta.env.VITE_KEYCLOAK_POST_LOGOUT_URI as string | undefined;

  if (!envUri) {
    return window.location.origin;
  }

  const envOrigin: string | null = (() => {
    try {
      return new URL(envUri).origin;
    } catch {
      return null;
    }
  })();

  if (
    envOrigin &&
    envOrigin.startsWith('http://localhost') &&
    envOrigin !== window.location.origin
  ) {
    return window.location.origin;
  }

  return envUri;
}

const KEYCLOAK_REDIRECT_URI = resolveRedirectUri();
const KEYCLOAK_POST_LOGOUT_URI = resolvePostLogoutUri();

// ============================================================
// State management
// ============================================================

const STATE_KEY = 'fc_oauth_state';
const PKCE_VERIFIER_KEY = 'fc_pkce_verifier';

function generateRandomString(length = 32): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

// ============================================================
// Redirect to Keycloak login (Authorization Code + PKCE)
// ============================================================

export async function redirectToKeycloakLogin(): Promise<void> {
  const state = generateRandomString(16);
  const codeVerifier = generateRandomString(32);
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);

  // Build query params — use encodeURIComponent for scope so spaces become
  // %20 (not +). Keycloak does NOT decode '+' as space in the scope parameter,
  // which causes "Invalid scopes: openid profile email" errors.
  const scope = 'openid profile email';
  const queryParams = new URLSearchParams({
    client_id: KEYCLOAK_CLIENT_ID,
    redirect_uri: KEYCLOAK_REDIRECT_URI,
    response_type: 'code',
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });

  const loginUrl =
    `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/auth` +
    `?${queryParams.toString()}` +
    `&scope=${encodeURIComponent(scope)}`;

  // NOTE: sessionStorage survives the SSO redirect to Keycloak and back
  // because the origin is the same. The org invite code (if present under
  // fc_org_invite_code) will still be available after the callback.
  // We intentionally do NOT clear sessionStorage here so that any
  // fc_org_invite_code set by LoginPage is preserved across the redirect.

  window.location.href = loginUrl;
}

// ============================================================
// Redirect to Keycloak registration (Authorization Code + PKCE)
// ============================================================

export async function redirectToKeycloakRegister(): Promise<void> {
  const state = generateRandomString(16);
  const codeVerifier = generateRandomString(32);
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);

  // Build query params — use encodeURIComponent for scope so spaces become
  // %20 (not +). Keycloak does NOT decode '+' as space in the scope parameter,
  // which causes "Invalid scopes: openid profile email" errors.
  const scope = 'openid profile email';
  const queryParams = new URLSearchParams({
    client_id: KEYCLOAK_CLIENT_ID,
    redirect_uri: KEYCLOAK_REDIRECT_URI,
    response_type: 'code',
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });

  const registerUrl =
    `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/registrations` +
    `?${queryParams.toString()}` +
    `&scope=${encodeURIComponent(scope)}`;

  // NOTE: sessionStorage survives the SSO redirect to Keycloak and back
  // because the origin is the same. The org invite code (if present under
  // fc_org_invite_code) will still be available after the callback.
  // We intentionally do NOT clear sessionStorage here so that any
  // fc_org_invite_code set by LoginPage is preserved across the redirect.

  window.location.href = registerUrl;
}

// ============================================================
// Handle Keycloak callback (exchange code for tokens via backend)
// ============================================================

export async function handleKeycloakCallback(
  code: string,
  state: string,
): Promise<AuthTokens | MfaRequiredResponse> {
  // Validate state to prevent CSRF
  const storedState = sessionStorage.getItem(STATE_KEY);
  if (state !== storedState) {
    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    throw new Error('Invalid OAuth state — possible CSRF attack');
  }

  const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY) || '';

  // Exchange code via backend — POST with code, codeVerifier, and redirectUri
  // The backend uses the PKCE verifier and redirect URI to exchange the code
  // with Keycloak's token endpoint.
  //
  // The api.post<T>() method returns the unwrapped payload T directly
  // (the response interceptor in client.ts strips the { success, data, message }
  // envelope added by the backend's TransformInterceptor).
  const result = await api.post<AuthTokens | MfaRequiredResponse>(authEndpoints.callback, {
    code,
    codeVerifier,
    redirectUri: KEYCLOAK_REDIRECT_URI,
    state,
  });

  // ── Check if MFA is required ─────────────────────────────────
  // If the backend returns mfaRequired: true, the response is an
  // MfaRequiredResponse and does NOT contain access/refresh tokens.
  // In this case, we do NOT store tokens or clean up PKCE — the caller
  // (auth.context.tsx) will handle the MFA challenge flow.
  if (result && typeof result === 'object' && (result as MfaRequiredResponse).mfaRequired === true) {
    return result as MfaRequiredResponse;
  }

  // ── Validate tokens before storing ──────────────────────────
  // If the access token is missing or not a string, something went wrong
  // with the response unwrapping or the backend did not return tokens.
  // Storing `undefined` would cause "jwt malformed" on the next API call.
  const tokens = result as AuthTokens;
  if (!tokens || typeof tokens.accessToken !== 'string' || !tokens.accessToken) {
    console.error(
      '[handleKeycloakCallback] Invalid token response from backend:',
      tokens,
    );
    throw new Error(
      'Authentication failed: backend did not return a valid access token. ' +
      'Check that the backend response is correctly unwrapped.',
    );
  }

  // Store tokens
  tokenStorage.setTokens(tokens.accessToken, tokens.refreshToken);

  // Clean up PKCE
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);

  return tokens;
}

// ============================================================
// Get current user from backend
// ============================================================

export async function fetchCurrentUser(): Promise<User> {
  return api.get<User>(authEndpoints.me);
}

// ============================================================
// Logout — clear local state and redirect to Keycloak logout
// ============================================================

export async function keycloakLogout(): Promise<void> {
  const refreshToken = tokenStorage.getRefreshToken();

  try {
    if (refreshToken) {
      await api.post(authEndpoints.logout, { refreshToken });
    }
  } catch {
    // Best-effort: even if backend logout fails, we clear local state
  } finally {
    tokenStorage.clearTokens();
  }

  // Redirect to Keycloak logout page
  const params = new URLSearchParams({
    client_id: KEYCLOAK_CLIENT_ID,
    post_logout_redirect_uri: KEYCLOAK_POST_LOGOUT_URI,
  });

  const logoutUrl = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/logout?${params.toString()}`;
  window.location.href = logoutUrl;
}

// ============================================================
// SSO Logout — destroy Keycloak SSO session without revoking tokens
// ============================================================

/**
 * Redirect to Keycloak's end-session endpoint to destroy the SSO session.
 *
 * Unlike `keycloakLogout()`, this function does NOT attempt to revoke tokens
 * or clear local token storage — it is used when the user was never fully
 * authenticated (e.g., they cancelled the MFA challenge) and therefore has
 * no access/refresh tokens to revoke. The only thing that needs to be cleaned
 * up is the Keycloak SSO session (browser cookie), so that the next login
 * attempt shows the Keycloak login page instead of silently re-using the
 * existing session.
 *
 * @param redirectUri - Where Keycloak should redirect after logout.
 *                      Defaults to `KEYCLOAK_POST_LOGOUT_URI`.
 */
export function keycloakSsoLogout(redirectUri?: string): void {
  const logoutUrl =
    `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/logout` +
    `?client_id=${KEYCLOAK_CLIENT_ID}` +
    `&post_logout_redirect_uri=${redirectUri || KEYCLOAK_POST_LOGOUT_URI}`;

  window.location.href = logoutUrl;
}

// ============================================================
// Check if we have valid tokens locally
// ============================================================

export function hasValidTokens(): boolean {
  return tokenStorage.hasToken();
}

// ============================================================
// Silent check: try to fetch /auth/me with current token
// ============================================================

export async function tryRestoreSession(): Promise<User | null> {
  if (!hasValidTokens()) return null;
  try {
    return await fetchCurrentUser();
  } catch {
    tokenStorage.clearTokens();
    return null;
  }
}

// ============================================================
// Organization invite code utilities
// ============================================================
// These functions manage the org invite code in sessionStorage.
// Since sessionStorage survives the SSO redirect to Keycloak and back
// (same origin), the code does not need to be appended to the OAuth URL.
// It simply persists across the redirect and is available after the callback.

const ORG_INVITE_CODE_KEY = 'fc_org_invite_code';

/**
 * Save the organization invite code to sessionStorage.
 * Called by LoginPage when the `?org=CODE` query param is present.
 */
export function saveOrgInviteCode(code: string): void {
  sessionStorage.setItem(ORG_INVITE_CODE_KEY, code);
}

/**
 * Read the organization invite code from sessionStorage.
 * Returns null if no code has been saved.
 * Called by OnboardingPage to check for auto-join.
 */
export function getOrgInviteCode(): string | null {
  return sessionStorage.getItem(ORG_INVITE_CODE_KEY);
}

/**
 * Remove the organization invite code from sessionStorage.
 * Called after auto-join (success or failure) in OnboardingPage,
 * after manual join in JoinOrganizationPage, or when the user
 * chooses a different onboarding path.
 */
export function clearOrgInviteCode(): void {
  sessionStorage.removeItem(ORG_INVITE_CODE_KEY);
}